import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Observable, of, ReplaySubject } from "rxjs";
import { WarehouseProduct } from "../../../shared/interface/warehouse-product";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FormHelperService } from "../../../shared/services/form-helper.service";
import { Router } from "@angular/router";
import {
  DictService,
  DYNAMIC_DICTIONARY_CATEGORY,
  DYNAMIC_DICTIONARY_WAREHOUSE
} from "../../../shared/services/dict.service";
import { HttpErrorResponse } from "@angular/common/http";
import { WarehouseService } from "../../../shared/services/warehouse.service";
import { first, takeUntil, tap } from "rxjs/operators";
import { Uuid } from "../../../shared/interface/response/uuid";
import { WarehouseProductAddDialogComponent } from "../warehouse-product-add-dialog/warehouse-product-add-dialog.component";
import { AvatarCropDialogComponent } from "../../user/avatar-crop-dialog/avatar-crop-dialog.component";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";

@Component({
  selector: 'app-warehouse-product-add',
  templateUrl: './warehouse-product-add.component.html',
  styleUrls: ['./warehouse-product-add.component.scss']
})
export class WarehouseProductAddComponent implements OnInit, OnDestroy {

  @Input() productObject?: WarehouseProduct;
  @Input() warehouseProduct: Observable<WarehouseProduct>;
  @Input() dialogRef?: MatDialogRef<WarehouseProductAddDialogComponent>;
  @Input() control?: FormControl;
  @Output() closeModification = new EventEmitter<void>();
  @Output() productCreated = new EventEmitter<string>();
  @ViewChild('imageSelect') imageSelect: ElementRef;

  readonly DYNAMIC_DICTIONARY_CATEGORY = DYNAMIC_DICTIONARY_CATEGORY;
  readonly DYNAMIC_DICTIONARY_WAREHOUSE = DYNAMIC_DICTIONARY_WAREHOUSE;

  productForm: FormGroup;
  processingRequest = false;

  categoryControl: FormControl;
  productNameControl: FormControl;
  productAmountControl: FormControl;
  productPriceControl: FormControl;
  warehouseControl: FormControl;
  purchaseDateControl: FormControl;
  expirationDateControl: FormControl;
  companyControl: FormControl;
  protocolControl: FormControl;
  noteControl: FormControl;
  formErrors = {};

  imageSelectControl = new FormControl('');
  imgCnt = 0;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private router: Router,
    private dialog: MatDialog,
    private dictService: DictService,
    private warehouseService: WarehouseService,
  ) {
    this.productForm = this.fb.group({
      category: this.categoryControl = fb.control('', [Validators.required]),
      productName: this.productNameControl = fb.control('', [Validators.required]),
      productAmount: this.productAmountControl = fb.control('', [Validators.required]),
      productPrice: this.productPriceControl = fb.control(''),
      warehouse: this.warehouseControl = fb.control(''),
      purchaseDate: this.purchaseDateControl = fb.control(''),
      expirationDate: this.expirationDateControl = fb.control(''),
      company: this.companyControl = fb.control(''),
      protocol: this.protocolControl = fb.control(false),
      note: this.noteControl = fb.control(''),
      filename: fb.control(''),
      content: fb.control(''),
    });
  }

  ngOnInit(): void {
    this.protocolControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isRequired: boolean) => {
        this.updatePurchaseValidators(!!isRequired);
      });

    if (this.warehouseProduct) {
      this.warehouseProduct
        .pipe(takeUntil(this.destroyed$))
        .subscribe((productObject: WarehouseProduct) => {
          this.productObject = productObject;
          this.initialize(productObject);
        });
    }

    this.imageSelectControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const selectedFile = this.imageSelect?.nativeElement?.files?.[0];
        if (!selectedFile) {
          return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(selectedFile);
        reader.onload = () => {
          const fileBase64 = reader.result.toString();
          const dialogRef = this.dialog.open(AvatarCropDialogComponent, {
            width: '55%',
            data: { fileBase64, ratio: 1, roundCropper: false, maintainRatio: false, compressedFile: false },
            height: '610px',
            maxHeight: '90%',
            panelClass: 'full-height-dialog',
          });

          dialogRef.afterClosed().subscribe(result => {
            if (this.imageSelect?.nativeElement) {
              this.imageSelect.nativeElement.value = '';
            }
            this.imageSelectControl.setValue('', { emitEvent: false });

            if (!result || !this.productObject?.uuid) {
              return;
            }

            this.warehouseService.updateImage(this.productObject.uuid, result)
              .subscribe(() => {
                this.warehouseService.getProduct(this.productObject.uuid)
                  .subscribe(product => {
                    this.productObject = product;
                    this.imgCnt++;
                  });

              });
          });
        };
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updatePurchaseValidators(isRequired: boolean): void {
    const controls = [
      this.productPriceControl,
      this.companyControl,
      this.purchaseDateControl,
    ];

    controls.forEach((control) => {
      if (isRequired) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity();
    });
  }

  initialize(productObject: WarehouseProduct): void {
    of(productObject)
      .pipe(
        tap(() => {
          this.productForm.reset({
            category: productObject.category?.id,
            productName: productObject.productName,
            productAmount: productObject.productAmount,
            productPrice: productObject.productPrice,
            warehouse: productObject.warehouse?.id,
            company: productObject.company?.id,
            purchaseDate: productObject.purchaseDate,
            expirationDate: productObject.expirationDate,
            protocol: productObject.protocol,
            note: productObject.note,
          });

          this.updatePurchaseValidators(!!productObject.protocol);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.initialized.next(true);
      });
  }

  sendForm(): void {
    if (!this.productForm.valid) {
      this.productForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;

    if (this.formHelper && this.formErrors) {
      this.formHelper.removeBackendErrorState(this.productForm, this.formErrors);
    }
    this.formErrors = {};

    const formData = this.productForm.getRawValue();

    const productAction = this.productObject?.uuid
      ? this.warehouseService.updateProduct(this.productObject.uuid, formData)
      : this.warehouseService.addProduct(formData);

    productAction.pipe(first()).subscribe(
      (response: Uuid) => {
        const message = this.productObject
          ? 'Pomyślnie zaktualizowano produkt'
          : 'Pomyślnie dodano produkt';

        this.snack.open(message, '', { duration: 2000 });
        this.processingRequest = false;

        if (this.productObject || this.dialogRef) {
          this.closeModification.emit();
          this.dialogRef?.close(true);
        } else {
          this.router.navigate(['/stan-magazynowy/szczegoly', response.uuid]);
        }
      },
      (error: HttpErrorResponse) => {
        this.processingRequest = false;

        if (error.status === 400) {
          this.formErrors = error.error?.error?.list || {};

          if (this.formHelper) {
            this.formHelper.setBackendErrorState(this.productForm, this.formErrors);
          }

          const errorMessage = error.error?.error?.text || 'Wystąpił błąd walidacji';
          this.snack.open(errorMessage, '400', { duration: 2000 });
        } else {
          const action = this.productObject ? 'aktualizacji' : 'tworzenia';
          this.snack.open(`Podczas ${action} produktu wystąpił błąd`, error.status.toString(), {
            duration: 2000,
          });
        }
      }
    );
  }

  cancel(): void {
    if (this.warehouseProduct) {
      this.closeModification.emit();
      return;
    }

    if (!this.dialogRef) {
      this.router.navigate(['/stan-magazynowy']);
    } else {
      this.dialogRef.close();
    }
  }

  setImage(): void {
    const e: HTMLElement = this.imageSelect.nativeElement;
    e.click();
  }

  deleteImage(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć zdjęcie?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.warehouseService.deleteImage(this.productObject.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto', '', {duration: 2000});

              if (this.productObject) {
                this.productObject.filename = null;
              }

              this.productForm.get('filename')?.setValue(null);
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 3000});
            }
          });
      }
    });
  }
}
